interface MobileCostBarProps {
  amount: number | null | undefined;
  label: string;
  targetId: string;
  savingsAmount?: number;
  enableShare?: boolean;
}

/**
 * Sticky bottom bar shown on mobile viewports only when a calculator has a result.
 * Tapping it scrolls to the full result panel identified by `targetId`.
 */
export function MobileCostBar({ amount, label, targetId, savingsAmount, enableShare = true }: MobileCostBarProps) {
  if (amount == null) return null;

  const handleClick = () => {
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleShare = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!enableShare || typeof navigator === 'undefined' || !('share' in navigator)) {
      return;
    }

    await navigator.share({
      title: `${label} estimate`,
      text: `AzureCalc estimate: £${amount.toFixed(2)}/month`,
      url: window.location.href,
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`View full cost breakdown: £${amount.toFixed(2)} per month`}
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden flex items-center justify-between gap-3 px-4 py-3 shadow-2xl"
      style={{ backgroundColor: 'var(--surface-container)', borderTop: '1px solid var(--outline-variant)' }}
    >
      <div className="flex min-w-0 flex-col items-start">
        <span className="text-[11px] font-mono text-outline uppercase tracking-wider truncate max-w-[180px]">{label}</span>
        {savingsAmount != null && savingsAmount > 0 && (
          <span className="mt-1 inline-flex items-center rounded border border-[#107c10]/30 bg-[#107c10]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#107c10]">
            Save £{savingsAmount.toFixed(2)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {enableShare && typeof navigator !== 'undefined' && 'share' in navigator && (
          <button
            type="button"
            onClick={handleShare}
            aria-label={`Share ${label} estimate`}
            className="flex h-[44px] w-[44px] items-center justify-center rounded border"
            style={{ borderColor: 'var(--outline-variant)', backgroundColor: 'var(--surface-container-low)' }}
          >
            <span className="material-symbols-outlined text-[18px]" style={{ color: 'var(--text-secondary)' }}>ios_share</span>
          </button>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold font-headline text-primary">
            £{amount.toFixed(2)}<span className="text-sm font-normal text-outline">/mo</span>
          </span>
          <span className="material-symbols-outlined text-outline text-[18px]">expand_less</span>
        </div>
      </div>
    </button>
  );
}
